import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";

import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/en/services/performance-marketing/google-ads")({
  head: () => {
    const h = pageHead("pmGoogleAds", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Google Ads Agency",
          description: "Search, Shopping, PMax, and Demand Gen managed for economic contribution, not platform recommendations.",
          url: "/en/services/performance-marketing/google-ads",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "Performance Marketing", path: "/en/services/performance-marketing" },
          { name: "Google Ads", path: "/en/services/performance-marketing/google-ads" },
        ])),
      ],
    };
  },
  component: GoogleAdsEN,
});

const steps = [
  { num: "01", title: "Analysis (3–5 days)", body: "Account audit, competitive analysis, tracking audit, feed quality check, customer lifetime value modeling where data is available. Output: concrete hypotheses where money is being left on the table economically — not marketing platitudes. No 80-page audits for self-profiling, but a focused assessment with concrete levers." },
  { num: "02", title: "Setup & Hardening (1–2 weeks)", body: "Hardening tracking (server-side, Enhanced Conversions, GA4 integration). Aligning account structure to economic logic — brand vs. non-brand, funnel stages, margin classes. Feed optimization via Delta. First bid strategy adjustments. Where technical setup is already clean, this goes faster — where not, here lies the biggest lever." },
  { num: "03", title: "Scaling (from week 2–3)", body: "Once the tracking foundation is in place: budget expansion in working areas, closure in unprofitable ones. PMax as volume lever with clearly delineated asset groups. Demand Gen for awareness extension where economically viable. Iteration cycles weekly, not monthly." },
  { num: "04", title: "Ongoing Operations (continuous)", body: "Monthly performance review with economic metrics, not platform metrics. Quarterly strategic adjustments. Operational management typically directly by the managing director — not by junior account managers with heavy mandate loads. Response times typically within one business day." },
];

const capabilities = [
  { name: "Search Campaigns", detail: "Brand · Non-Brand · Long-Tail" },
  { name: "Shopping & PMax", detail: "Feed hygiene via Delta" },
  { name: "YouTube & Demand Gen", detail: "Funnel logic · view-through" },
  { name: "Conversion Tracking", detail: "Server-side · Enhanced · GA4" },
  { name: "Smart Bidding Management", detail: "tROAS · tCPA · learning discipline" },
  { name: "Audience Strategies", detail: "Customer Match · remarketing" },
  { name: "Negative-List Hygiene", detail: "Rule-based · daily" },
  { name: "Reporting & Management", detail: "Looker Studio · C-level calls" },
];

const audience = [
  { tag: "Primary", title: "B2C E-Commerce", body: "Most important performance channel for most e-commerce setups. Search serves demand, Shopping serves comparison, PMax scales. Sensible from ~€5,000/month, fully scalable to six-figure budgets." },
  { tag: "Secondary", title: "B2B Lead Generation", body: "Search works for B2B when buying intent maps to searchable language — SaaS, IT, consulting. With long sales cycles, LinkedIn is often the better first channel; Google complements bottom funnel." },
  { tag: "Tertiary", title: "SaaS Trial Sign-ups", body: "Works when trial conversion is cleanly tracked and CAC is clearly defined. Search for branded and bottom funnel, Demand Gen for awareness." },
  { tag: "Disqualification", title: "When we are not the right partner", body: "Monthly budget under €2,500 — operational effort exceeds the lever. Expectation 'double my ROAS' — we make no promises. If tracking can't be cleaned up — without clean data, performance is gambling." },
];

const realistic = [
  "Efficiency improvements of 20–40% in the first year are realistic in most mandates — especially where tracking, account structure, or feed quality were historically neglected.",
  "Cost-per-acquisition reduction of 15–30% in B2C e-commerce within 3–6 months is a typical range when previously working with unchecked platform recommendations.",
  "Scaling leverage of 2–5x at the same ROAS is possible when customer economics support it. Scaling without an economic check isn't a service, it's risk.",
  "Brand-search share reduction of 10–25% at the same revenue is a typical lever when brand and non-brand have historically been mixed.",
];

const value = [
  ["Economic clarity instead of platform vanity.", "You know which euro of spend creates which contribution margin — not which click had which CTR."],
  ["Clean data foundation as control prerequisite.", "Server-side tracking, Enhanced Conversions, and GA4 aren't checked off — they're maintained as economic assets."],
  ["Proprietary tools as scaling leverage.", "Delta for feeds eliminates Excel ping-pong. picazzoo solves the creative bottleneck in PMax and Demand Gen."],
  ["Operational management by experienced hand.", "Directly by the managing director — not by junior account managers carrying 30 mandates each."],
  ["Compensation model without budget coupling.", "We don't earn more when you spend more. The typical conflict of interest disappears."],
  ["Transparent, auditable approach.", "You understand why what happens. No black box, no platform myths."],
];

const related = [
  { to: "/en/services/performance-marketing/meta-ads", name: "Meta Ads", sub: "Social media performance on Facebook & Instagram" },
  { to: "/en/services/tracking-data-foundation", name: "Tracking & Data Foundation", sub: "Clean data as prerequisite for any Google Ads optimization" },
  { to: "/en/services/delta", name: "Delta", sub: "Our feed-management tool for Shopping and PMax" },
];

const channelLinks = [
  { to: "/en/services/performance-marketing/microsoft-ads", name: "Microsoft Advertising", sub: "Bing search, Edge and LinkedIn audiences — search-mix diversification next to Google." },
  { to: "/en/services/performance-marketing/meta-ads", name: "Advertising on Facebook & Instagram", sub: "Performance on Instagram and Facebook with creative discipline." },
  { to: "/en/services/performance-marketing/affiliate-marketing", name: "Affiliate Marketing", sub: "Partner-driven performance with its own tracking and commission discipline." },
];

function GoogleAdsEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · Google Ads"
          headline={<>Google Ads Agency — <em>economically managed,</em> not platform-driven.</>}
          lead="Search, Shopping, PMax, and Demand Gen — managed for economic contribution, not for platform recommendations. Calvarius has worked as a Google Ads agency for e-commerce, B2B, and SaaS since 2013 — with our own feed-management tool and clean server-side tracking as a control foundation."
          metaStrip={["In the channel since 2013", "Budgets up to €200K/month", "Own feed tool"]}
          primaryCta={{
            eyebrow: "Free · approx. 30 minutes · no commitment",
            label: "Request a strategy conversation",
            to: "/en/contact",
          }}
          secondaryCta={{
            label: "Or: download the Performance Marketing Checklist without signup",
            to: "/en/tools/performance-marketing-checklist",
          }}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Performance Marketing", to: "/en/services/performance-marketing" },
            { label: "Google Ads" },
          ]}
        />

        <Section number="02" kicker="Standpoint" headline={<>What a Google Ads agency must <em>truly deliver today.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-8">Anyone looking for a Google Ads agency usually has a concrete economic question in mind: will I get the return for my ad budget that the platform promises? The honest answer, after over ten years as a Google Ads agency: not without discipline at the control level. Performance Max takes control away, Smart Bidding hides logic behind algorithms, and account recommendations often serve Google's interests, not the advertiser's.</p>

              <h3 className="mt-10 mb-4 font-display text-2xl text-ink">Google's interests are not your interests.</h3>
              <p className="mb-5">That's not a conspiracy charge — it's economic reality: Google is a publicly traded company with a clear mandate for revenue and profit maximization. Every recommendation, every auto-apply logic, every default in the platform is ultimately trained to optimize <em>Google's</em> KPIs — auction utilization, average click prices, inventory usage across all Google properties. The advertiser KPI "as much contribution margin per euro spend as possible" does not necessarily lie in the same optimization direction.</p>
              <p className="mb-4">Concrete examples where Google's interest and yours diverge:</p>
              <ul className="mb-5 space-y-3 pl-5 list-disc marker:text-cyan-deep">
                <li><strong className="font-semibold text-ink">Auto-apply recommendations</strong> shift budget into areas that improve Google's auction utilization — not necessarily into areas that economically carry for you.</li>
                <li><strong className="font-semibold text-ink">Performance Max as default recommendation</strong> maximizes inventory usage across Search, Display, YouTube, and Gmail simultaneously — even where the individual channel slot delivers no economic contribution.</li>
                <li><strong className="font-semibold text-ink">Broad match plus smart bidding</strong> as "current best practice" significantly broadens the served search queries — which raises Google's inventory sales but buys in wastage.</li>
                <li><strong className="font-semibold text-ink">"You don't need keywords anymore"</strong> as a recurring sales line from Google account managers — convenient for Google's ML training, but risky for advertisers without clean conversion substance.</li>
              </ul>
              <p className="mb-8">If you blindly trust the system and click "apply recommendations," you lose control over economic viability. If you run Google Ads today the way you did in 2018, you burn money. Calvarius positions itself between these two extremes.</p>

              <h3 className="mt-10 mb-4 font-display text-2xl text-ink">We use the machine advantages — but we don't hand Google the steering wheel.</h3>
              <p className="mb-5">We deploy automated bidding strategies where they operate on clean data substance. We use Performance Max as a volume lever, but only within a differentiated account structure with clearly delineated asset groups, audience signals, and cannibalization reports against the other campaign types. We use Smart Shopping as an efficiency tool, but check before each bid-strategy migration whether the conversion data substance even carries the model.</p>
              <p className="mb-8">Clicks are not a success metric. Conversions are not a success metric. The <strong className="font-semibold text-ink">contribution margin per euro invested</strong> is the success metric. This metric is not visible by default in Google Ads — we make it visible, with our own analytics and AI tools that operate independently of Google's recommendation layer. They give us visibility into what Google deliberately doesn't show: cannibalization between campaign types, the actual search-term substance behind broad match, spend drift inside Performance Max asset groups, methodological assessment of new Google beta features for mandate suitability.</p>

              <p>Concretely, that means: We build conversion tracking not as a compulsory setup, but as an economic data foundation. We use Enhanced Conversions for higher data quality. We cleanly separate brand and non-brand performance instead of drowning in the account aggregate. We review monthly which campaigns are still running at positive contribution and which aren't — and shut off without sentimentality what doesn't work. Our own feed management through our tool <Link to="/en/services/delta" className="underline">Delta</Link> ensures shopping and PMax campaigns work with clean, platform-specifically optimized product data — without Excel ping-pong and without one-size-fits-all feeds.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Audience fit" headline={<>When you need a Google Ads agency — <em>and when you don't.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodology" headline={<>How our Google Ads agency <em>starts mandates.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Fast start as competitive advantage</p>
              <p className="text-[15px] leading-relaxed text-ink-2">While classic large agencies often need six to twelve weeks before the first optimized setup goes live, we are productive in the market within two to three weeks. This is possible because we don't manage stakeholder layers but work directly operationally.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="What we deliver" headline={<>What our Google Ads agency <em>concretely delivers.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools and stack of our <em>Google Ads management.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native platform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Google Ads UI &amp; Editor</li>
                  <li>Google Merchant Center</li>
                  <li>Performance Planner</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Google Tag Manager (Web + Server)</li>
                  <li>Google Analytics 4 + BigQuery</li>
                  <li>Server-side tracking</li>
                  <li>Enhanced Conversions</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/en/services/delta" className="font-medium text-ink hover:text-cyan-deep">Delta →</Link><br/><span className="text-ink-3">Feed management for Shopping &amp; PMax</span></li>
                  <li><Link to="/en/services/picazzoo" className="font-medium text-ink hover:text-cyan-deep">picazzoo →</Link><br/><span className="text-ink-3">AI asset production for PMax &amp; Demand Gen</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="What's realistic" headline={<>Realistic results of a <em>Google Ads optimization.</em></>}
          caption="These ranges are empirical. We make no promises — serious performance management works with the concrete situation, not advertising claims.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="What you gain" headline={<>What you get from our <em>Google Ads agency.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="en" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Go deeper" headline={<>Related disciplines.</>}
          caption="These topics work in interplay.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/en/services/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← All performance marketing disciplines
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Get concrete" headline={<>Get concrete — <em>free and methodical.</em></>}>
          <RevealOnScroll>
            <p className="mb-10 max-w-2xl text-[17px] leading-relaxed text-ink-2">Google Ads is not abstract — it lives off your concrete situation. Three ways to see whether Calvarius methodology fits your setup.</p>

            <div className="grid gap-6 lg:grid-cols-3">
              {/* Primary */}
              <div className="rounded-lg border-2 border-lime bg-white p-7 lg:col-span-2">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-lime-deep">Free · approx. 30 minutes · no commitment</p>
                <h3 className="mt-4 font-display text-2xl text-ink md:text-3xl">Request a strategy conversation</h3>
                <p className="mt-4 text-[15px] leading-relaxed text-ink-3">In an initial conversation we clarify whether Google Ads makes economic sense in your setup — and if so, with what priority. No sales pitch, no commitment.</p>
                <Link to="/en/contact" className="mt-6 inline-flex items-center gap-2 rounded-md bg-lime px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-ink transition-colors hover:bg-lime/90">
                  Request a strategy conversation →
                </Link>
              </div>

              {/* Secondary */}
              <div className="rounded-lg border border-rule bg-white p-7">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Free · no signup · immediately available</p>
                <h3 className="mt-4 font-display text-xl text-ink">Performance Marketing Checklist</h3>
                <p className="mt-4 text-sm leading-relaxed text-ink-3">Methodical setup sequence in seven phases — the substance we apply in our own mandates, as a PDF for self-audit.</p>
                <Link to="/en/tools/performance-marketing-checklist" className="mt-6 inline-flex items-center font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                  Download checklist →
                </Link>
              </div>
            </div>

            {/* Tertiary */}
            <div className="mt-8 border-t border-rule pt-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3">Also interesting</p>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <Link to="/en/tools/profitability-calculator" className="font-display text-lg text-ink hover:text-cyan-deep">Profitability Calculator →</Link>
                <span className="text-sm text-ink-3">Max-CPC calculation from AOV, margin, and conversion rate. Takes two minutes.</span>
              </div>
            </div>
          </RevealOnScroll>
        </Section>
        <GlossaryTerms locale="en" ids={["smart-bidding", "target-cpa", "target-roas", "performance-max", "quality-score", "budget-pacing"]} />
      </main>
      <Footer />
    </>
  );
}
